import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout, getFitnessScore } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import {
  Brain, MessageCircle, Target, TrendingUp, ArrowRight,
  Calculator, Wallet, BookOpen, Activity, ChevronRight, Sparkles, Share2, CalendarCheck, Quote
} from 'lucide-react';
import axios from 'axios';
import { WHATSAPP_URL, INVITE_WHATSAPP_URL } from '../config/contact';

const GOLD = '#C0975C';
const heading = { fontFamily: "'Outfit', sans-serif" };

const featureCards = [
  { path: '/courses', label: 'Financial Courses', icon: BookOpen, desc: 'Guided lessons across every money topic' },
  { path: '/quiz', label: 'Quizzes', icon: Brain, desc: 'Test and sharpen your knowledge' },
  { path: '/daily-quiz', label: 'Daily Quiz', icon: CalendarCheck, desc: '10 fresh questions every day' },
  { path: '/budget-tracker', label: 'Cashflow Overview', icon: Wallet, desc: 'See where your money goes' },
  { path: '/investment-calculator', label: 'Financial Modelling', icon: TrendingUp, desc: 'Model growth, goals and scenarios' },
  { path: '/mentor', label: 'Precision Assistant', icon: Sparkles, desc: 'Ask your money questions, any time' },
  { path: '/tools', label: 'Money Tools', icon: Calculator, desc: 'Practical calculators and utilities' },
  { path: '/goals', label: 'My Goals', icon: Target, desc: 'Set and track financial goals' },
  { path: '/health', label: 'Health Check', icon: Activity, desc: 'A snapshot of your current finances' },
];

const getGreeting = (username) => {
  const hour = new Date().getHours();
  const name = username?.split(' ')[0] || 'there';
  if (hour < 12) return `Good morning, ${name}`;
  if (hour < 17) return `Good afternoon, ${name}`;
  return `Good evening, ${name}`;
};

export default function Dashboard() {
  const { user } = useAuth();
  const greeting = getGreeting(user?.username);
  const fitnessScore = getFitnessScore(user);
  const [quote, setQuote] = useState(null);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/daily/quote`).then((r) => setQuote(r.data)).catch(() => {});
  }, []);

  return (
    <Layout>
      <div className="p-5 lg:p-10 max-w-7xl mx-auto">

        {/* Hero / Fitness Score */}
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-10">
          <div className="relative overflow-hidden rounded-3xl border border-[#1D3D72]" data-testid="dashboard-hero">
            <img
              src="https://images.unsplash.com/photo-1525945995886-0ede2bd49ab6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
              alt="Cape Town at dawn"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#061530] via-[#0A1F44]/90 to-[#0A1F44]/40" />
            <div className="relative p-8 lg:p-14">
              <p style={{ color: GOLD }} className="text-xs tracking-[0.35em] uppercase mb-3">Welcome back</p>
              <h1 style={heading} className="text-4xl lg:text-6xl font-extrabold text-white mb-8 tracking-tight leading-[1.05]">{greeting}</h1>

              <div className="max-w-xl pw-glass rounded-2xl p-6">
                <div className="flex items-end justify-between mb-3">
                  <div className="flex items-center gap-2 text-white">
                    <Activity className="w-5 h-5" style={{ color: GOLD }} />
                    <span className="text-sm uppercase tracking-wider">Financial Fitness Score</span>
                  </div>
                  <span className="font-mono text-4xl font-bold" style={{ color: GOLD }}>
                    {fitnessScore}<span className="text-base text-[#94A9C9]">/100</span>
                  </span>
                </div>
                <div className="h-3 w-full overflow-hidden rounded-full bg-[#061530]">
                  <motion.div
                    className="h-full rounded-full"
                    style={{ background: `linear-gradient(90deg, ${GOLD}, #D4B37F)` }}
                    initial={{ width: 0 }}
                    animate={{ width: `${fitnessScore}%` }}
                    transition={{ duration: 1.1, ease: 'easeOut' }}
                  />
                </div>
                <p className="text-sm mt-3 text-[#94A9C9]">
                  Complete courses, quizzes and tools to grow your score and your confidence.
                </p>
                <a
                  href={INVITE_WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="invite-friends-btn"
                  className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 rounded-full text-sm font-semibold bg-white/10 text-white hover:bg-white/15 transition-colors border border-[#C0975C]/30"
                >
                  <Share2 className="w-4 h-4 text-[#C0975C]" /> Invite a friend on WhatsApp
                </a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Daily money quote */}
        {quote && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mb-10">
            <div className="relative rounded-2xl border border-[#C0975C]/30 bg-[#122C5A] p-6 lg:p-7 overflow-hidden" data-testid="daily-quote">
              <Quote className="w-8 h-8 text-[#C0975C] opacity-50 mb-3" />
              <p className="text-lg lg:text-xl text-white leading-relaxed" style={heading}>"{quote.quote}"</p>
              <p className="text-sm text-[#C0975C] mt-3 font-semibold">{quote.author}</p>
              <span className="absolute top-5 right-6 text-[11px] uppercase tracking-widest text-[#94A9C9]">Quote of the day</span>
            </div>
          </motion.div>
        )}

        {/* Feature Grid */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-10">
          <div className="flex items-end justify-between mb-6">
            <h2 style={heading} className="text-2xl lg:text-3xl font-bold text-white tracking-tight">Continue building</h2>
            <span className="text-sm text-[#94A9C9] hidden sm:block">Pick up where you left off</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featureCards.map((card, i) => (
              <motion.div
                key={card.path}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.05 }}
              >
                <Link
                  to={card.path}
                  data-testid={`dashboard-card-${card.path.replace('/', '')}`}
                  className="neo-card group block h-full p-7"
                >
                  <div className="w-12 h-12 flex items-center justify-center mb-5 rounded-xl bg-[#0A1F44] border border-[#C0975C]/25 group-hover:bg-[#C0975C] transition-colors">
                    <card.icon className="w-6 h-6 text-[#C0975C] group-hover:text-[#0A1F44] transition-colors" />
                  </div>
                  <h3 style={heading} className="text-lg font-bold text-white mb-1.5">{card.label}</h3>
                  <p className="text-sm leading-relaxed text-[#94A9C9]">{card.desc}</p>
                  <div className="mt-5 flex items-center gap-1 text-sm font-semibold text-[#C0975C] group-hover:gap-2 transition-all">
                    Open <ChevronRight className="w-4 h-4" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Adviser CTA */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
          <div className="relative overflow-hidden rounded-3xl border border-[#C0975C]/40 bg-gradient-to-r from-[#122C5A] to-[#0A1F44] p-8 lg:p-10">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#C0975C]/15 flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-6 h-6" style={{ color: GOLD }} />
                </div>
                <div>
                  <h3 style={heading} className="text-2xl font-bold text-white mb-1.5">Ready for advice tailored to you?</h3>
                  <p className="text-sm text-[#94A9C9] max-w-lg">
                    Our Assistant shares general tips only. For personal financial advice, chat to a qualified Precision Wealth adviser on our WhatsApp Hotline.
                  </p>
                </div>
              </div>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="dashboard-adviser-cta"
                className="neo-button-primary inline-flex items-center justify-center gap-2 px-8 py-4 whitespace-nowrap"
              >
                <MessageCircle className="w-4 h-4" /> Advisor WhatsApp Hotline
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
}
